<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePreferredReducedMotion } from '@vueuse/core';
import Draggable from 'vuedraggable';
import NextButton from 'dashboard/components-next/button/Button.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import KanbanCard from './KanbanCard.vue';

const props = defineProps({
  stage: { type: Object, required: true },
  column: { type: Object, required: true },
  // Compartilhado entre todas as colunas do MESMO funil, para o
  // vuedraggable aceitar soltar um card vindo de outra coluna (PRD T3.4
  // secao 4: "arrastar entre colunas").
  dragGroup: { type: String, required: true },
});

const emit = defineEmits(['change', 'loadMore']);

const { t } = useI18n();
const scrollContainer = ref(null);

// PRD T3.4 secao 4: prefers-reduced-motion desliga a animação de arrasto
// (o FLIP que o vuedraggable/SortableJS aplica via `animation`), exceto
// opacidade — card se movendo sozinho na tela é gatilho vestibular real.
const prefersReducedMotion = usePreferredReducedMotion();
const dragAnimationMs = computed(() =>
  prefersReducedMotion.value === 'reduce' ? 0 : 200
);

const isEmpty = computed(() => props.column.cards.length === 0);

// ALTO (revisor-frontend, 1.3.1): liga o título da coluna à lista de cards
// que ela rotula — sem isto não há relação programática entre o cabeçalho e
// os cards, só a proximidade visual.
const headingId = computed(() => `kanban-column-heading-${props.stage.id}`);

const intersectionOptions = computed(() => ({
  root: scrollContainer.value,
  rootMargin: '100px 0px 100px 0px',
}));

const onLoadMore = () => emit('loadMore', props.stage.id);
</script>

<template>
  <div
    class="flex flex-col flex-1 min-w-[240px] max-w-[420px] h-full rounded-lg bg-n-alpha-1"
  >
    <!--
      Coluna ELÁSTICA, não 280px fixos: `flex-1` (flex: 1 1 0%) reparte a
      largura livre do board entre as colunas em vez de deixá-la sobrando à
      direita — em 1920, cinco colunas passam de 280px cada (279px ociosos)
      a 327px cada, com 0 ocioso (medido em Chrome; ver EVIDENCIA do
      retorno). `min-w` é o piso onde o cartão ainda é legível: abaixo dele
      o board volta a rolar na horizontal, como já rolava. `max-w` evita a
      coluna gigante do funil de duas etapas em tela larga.

      O comentário fica DENTRO da raiz de propósito: comentário antes do
      elemento raiz transforma o componente em fragmento (múltiplas raízes),
      e aí ele perde o repasse automático de atributos e wrapper.classes()
      passa a ler o nó de comentário. Medido: a asserção de `flex-1`
      reprovava com "expected [] to include".
    -->
    <div
      class="flex items-center justify-between gap-2 px-3 py-2.5 border-b shrink-0 border-n-weak"
    >
      <!--
        ALTO (revisor-frontend, heading-order): quando o board tem colunas
        (showColumns em KanbanBoardIndex.vue), este é o único cabeçalho
        visível abaixo do h1 — os h2 da página só existem nos estados
        mutuamente exclusivos (erro, sem funil, vazio por filtro), e a
        hierarquia pulava direto para h3 nesse caminho. h2 aqui mantém o
        board inteiro em h1 -> h2 sem redesenhar a hierarquia visual (o
        tamanho continua vindo das classes, não da tag).

        O cabeçalho tinha o MESMO peso visual do rodapé de um card
        (text-xs, slate-11): nada na tela dizia onde uma coluna começava.
        text-sm + slate-12 é subida de tamanho E de contraste — slate-12 é
        o token de texto primário, então a mudança não pode reprovar o
        contraste que slate-11 já passava. O uppercase saiu porque nome de
        etapa é longo ("AGUARDANDO PAGAMENTO") e caixa alta reduz a
        legibilidade justamente onde a coluna é mais estreita.
      -->
      <h2
        :id="headingId"
        class="m-0 text-sm font-semibold truncate text-n-slate-12"
        :title="stage.name"
      >
        {{ stage.name }}
      </h2>
      <span class="text-xs font-medium tabular-nums shrink-0 text-n-slate-11">
        {{
          column.countCapped
            ? t('KANBAN.BOARD.COLUMN.COUNT_CAPPED', { count: column.count })
            : column.count
        }}
      </span>
    </div>

    <div
      ref="scrollContainer"
      class="flex-1 min-h-0 px-2 pt-2 pb-2 overflow-y-auto"
    >
      <!-- O Draggable fica SEMPRE montado, mesmo com a coluna vazia: com
           v-if/v-else por cima dele, `cards.length === 0` desmontava o
           componente inteiro, e sem instância SortableJS no grupo a coluna
           não recebe drop (C2). min-h-full estende a área de soltar até a
           altura inteira da coluna, não só a altura do conteúdo — soltar no
           espaço em branco abaixo do último card também tem que funcionar.

           ALTO (revisor-frontend, 1.3.1): role="list" + aria-labelledby liga
           esta lista ao h2 acima (vuedraggable repassa "role" e atributos
           "aria-*" para o elemento raiz renderizado — não são opções do
           Sortable, ver isHtmlAttribute em vuedraggable/src/util/tags.js).
           Cada KanbanCard já é role="listitem" (KanbanCard.vue), e o papel
           de lista só existe quando há cards — ver o comentário do #footer. -->
      <Draggable
        :list="column.cards"
        :group="dragGroup"
        :sort="false"
        :animation="dragAnimationMs"
        ghost-class="opacity-40"
        item-key="id"
        :role="isEmpty ? undefined : 'list'"
        :aria-labelledby="isEmpty ? undefined : headingId"
        class="flex flex-col gap-2 min-h-full"
        @change="emit('change', $event)"
      >
        <template #item="{ element }">
          <KanbanCard :card="element" :stage="stage" />
        </template>
        <!--
          Coluna vazia não é uma lista vazia: é a ausência de lista. As duas
          tentativas anteriores mantinham role="list" sempre e discutiam o
          papel do <p>, e as duas erravam de um lado:
            - role="presentation" no <p>: a lista ficava sem NENHUM filho
              válido e reprovava `aria-required-children` (WCAG 2.0 A,
              1.3.1), 1 nó medido no estado vazio;
            - role="listitem" no <p>: fechava o critério (0 violações, 5
              passes) ao preço de a árvore AX anunciar "lista, 1 item" numa
              coluna com ZERO conversas — o axe aprovava e a contagem mentia.
          Sem o papel de lista no estado vazio, não há filho exigido a
          reprovar nem contagem a mentir, e o <p> continua no slot #footer:
          fora do Draggable ele viraria irmão de um elemento com min-h-full
          (100% da coluna) e seria empurrado para fora da área visível.
          O h2 da coluna segue rotulando a região.
        -->
        <template v-if="isEmpty" #footer>
          <p class="m-0 px-2 py-4 text-xs text-center text-n-slate-11">
            {{ t('KANBAN.BOARD.COLUMN.EMPTY', { stage: stage.name }) }}
          </p>
        </template>
      </Draggable>

      <IntersectionObserver
        v-if="column.hasMore"
        :options="intersectionOptions"
        @observed="onLoadMore"
      />

      <div
        v-if="column.isLoadingMore"
        class="py-2 text-xs text-center text-n-slate-11"
      >
        {{ t('KANBAN.BOARD.LOADING') }}
      </div>

      <div
        v-else-if="column.loadMoreError"
        class="flex flex-col items-center gap-1 py-2"
      >
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('KANBAN.BOARD.COLUMN.LOAD_MORE_ERROR') }}
        </p>
        <NextButton
          xs
          faded
          slate
          :label="t('KANBAN.BOARD.COLUMN.RETRY')"
          @click="onLoadMore"
        />
      </div>
    </div>
  </div>
</template>
